import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, Exercise, MuscleGroup } from '../database';
import { seedExerciseLibrary, migrateExercisesWithMuscleGroups, addNewExercisesFromLibrary, migrateToConsistentExerciseIds } from '../utils/seedData';

const Exercises: React.FC = () => {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedMuscle, setSelectedMuscle] = useState<string | 'All'>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    loadExercises();
  }, []);

  const loadExercises = async () => {
    try {
      const allExercises = await db.exercises.orderBy('name').toArray();
      setExercises(allExercises);
    } catch (error) {
      console.error('Failed to load exercises:', error);
    }
  };


  const handleDeleteExercise = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this exercise?')) {
      try {
        await db.exercises.delete(id);
        await loadExercises();
      } catch (error) {
        console.error('Failed to delete exercise:', error);
      }
    }
  };


  const handleSeedExercises = async () => {
    try {
      await seedExerciseLibrary();
      await loadExercises();
    } catch (error) {
      console.error('Failed to seed exercises:', error);
      alert('Failed to add exercise library. Check console for details.');
    }
  };

  const handleMigrateExercises = async () => {
    try {
      await migrateExercisesWithMuscleGroups();
      await loadExercises();
      alert('Successfully updated exercises with muscle group data!');
    } catch (error) {
      console.error('Failed to migrate exercises:', error);
      alert('Failed to update exercises. Check console for details.');
    }
  };

  const handleAddNewExercises = async () => {
    try {
      await addNewExercisesFromLibrary();
      await loadExercises();
      alert('Successfully added new exercises from library!');
    } catch (error) {
      console.error('Failed to add new exercises:', error);
      alert('Failed to add new exercises. Check console for details.');
    }
  };

  const handleMigrateExerciseIds = async () => {
    if (!window.confirm(
      '🚨 MIGRATE TO CONSISTENT EXERCISE IDs\n\n' +
      'This will:\n' +
      '✅ PRESERVE all your workout history and progression\n' +
      '✅ Enable proper routine sharing with other users\n' +
      '✅ Fix exercise IDs to match the standard library\n\n' +
      'This is a ONE-TIME migration. Continue?'
    )) {
      return;
    }

    try {
      await migrateToConsistentExerciseIds();
      await loadExercises();
      alert('🎉 Migration successful! Your data is preserved and routine sharing now works properly.');
    } catch (error) {
      console.error('Failed to migrate exercise IDs:', error);
      alert('❌ Migration failed. Check console for details. Your data is unchanged.');
    }
  };


  const categories = ['All', 'Push', 'Pull', 'Legs', 'Core', 'Cardio', 'Functional', 'Machine'];
  
  const muscles: (MuscleGroup | 'All')[] = [
    'All', 'Chest', 'Back', 'Lats', 'Rhomboids', 'Traps', 'Shoulders',
    'Front Delts', 'Side Delts', 'Rear Delts', 'Biceps', 'Triceps', 'Forearms',
    'Core', 'Abs', 'Obliques', 'Quads', 'Hamstrings', 'Glutes', 'Calves',
    'Cardiovascular', 'Full Body'
  ];
  
  // Don't filter if data hasn't loaded yet
  const filteredExercises = exercises ? exercises.filter(ex => {
    // Apply search filter
    const matchesSearch = searchTerm === '' || 
      ex.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ex.description && ex.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Apply category filter
    const matchesCategory = selectedCategory === 'All' || ex.category === selectedCategory;
    
    // Apply muscle filter
    const matchesMuscle = selectedMuscle === 'All' || 
      ex.primaryMuscles?.includes(selectedMuscle as MuscleGroup) ||
      ex.secondaryMuscles?.includes(selectedMuscle as MuscleGroup);
    
    return matchesSearch && matchesCategory && matchesMuscle;
  }) : [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">
          Exercise Library ({filteredExercises.length})
        </h2>
        <div className="flex gap-2 flex-wrap">
          {exercises.length > 0 && (
            <>
              <button
                onClick={handleMigrateExerciseIds}
                className="bg-orange-600 text-white px-3 py-2 rounded text-sm hover:bg-orange-700"
                title="ONE-TIME: Migrate to consistent exercise IDs for routine sharing"
              >
                🔧 Fix Exercise IDs
              </button>
              <button
                onClick={handleAddNewExercises}
                className="bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700"
                title="Add new exercises from updated library"
              >
                📚 Add New Exercises
              </button>
              <button
                onClick={handleMigrateExercises}
                className="bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700"
                title="Add muscle group data to existing exercises"
              >
                Update Muscle Groups
              </button>
            </>
          )}
          <Link
            to="/exercises/new"
            className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700"
          >
            Add Exercise
          </Link>
        </div>
      </div>

      {/* Search and Category Filter */}
      {exercises.length > 0 && (
        <div className="space-y-3">
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search exercises..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 pl-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="absolute left-2.5 top-2.5 text-gray-400">
              🔍
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          
          {/* Category Filter */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Category</h4>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          
          {/* Muscle Filter */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Muscle Group</h4>
            <div className="flex flex-wrap gap-2">
              {muscles.map(muscle => (
                <button
                  key={muscle}
                  onClick={() => setSelectedMuscle(muscle)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    selectedMuscle === muscle
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {muscle}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {exercises.length === 0 ? (
        <div className="text-center text-gray-500 py-8 space-y-4">
          <p>No exercises yet. Add your first exercise!</p>
          <button
            onClick={handleSeedExercises}
            className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm"
          >
            📚 Load Exercise Library (140+ exercises)
          </button>
        </div>
      ) : filteredExercises.length === 0 ? (
        <div className="text-center text-gray-500 py-8 space-y-2">
          <p>No exercises match your search and filters.</p>
          <div className="space-x-2">
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-blue-600 text-sm"
              >
                Clear search
              </button>
            )}
            {selectedCategory !== 'All' && (
              <button
                onClick={() => setSelectedCategory('All')}
                className="text-blue-600 text-sm"
              >
                Clear category
              </button>
            )}
            {selectedMuscle !== 'All' && (
              <button
                onClick={() => setSelectedMuscle('All')}
                className="text-green-600 text-sm"
              >
                Clear muscle filter
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredExercises.map((exercise) => (
            <div
              key={exercise.id}
              className="bg-white border rounded-lg p-4"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{exercise.name}</h3>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {exercise.category && (
                      <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded font-medium">
                        {exercise.category}
                      </span>
                    )}
                    {exercise.primaryMuscles?.map(muscle => (
                      <span key={muscle} className="inline-block bg-blue-500 text-white text-xs px-2 py-1 rounded font-medium">
                        {muscle}
                      </span>
                    ))}
                    {exercise.secondaryMuscles?.map(muscle => (
                      <span key={muscle} className="inline-block bg-green-500 text-white text-xs px-2 py-1 rounded">
                        {muscle}
                      </span>
                    ))}
                  </div>
                  {exercise.description && (
                    <p className="text-gray-600 text-sm mt-2">{exercise.description}</p>
                  )}
                </div>
                <div className="flex space-x-2 ml-4">
                  <Link
                    to={`/exercises/${exercise.id}`}
                    className="text-blue-600 text-sm"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDeleteExercise(exercise.id!)}
                    className="text-red-600 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Exercises;